/**
 * API Configuration
 *
 */

import { shouldRetryApiError } from '@/utils/apiRetry'

// Export current configuration
export const apiConfig = {
  baseUrl: `${import.meta.env.VITE_API_BASE_URL}/v1`,
  timeout: 30000,
  retries: {
    attempts: 3,
    delay: 1000,
    backoff: 'exponential',
    retryCondition: shouldRetryApiError,
  },
  cache: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  },
  enableLogging: true,
}

// API Endpoints
export const API_ENDPOINTS = {
  // Authentication & User Management
  DISPATCHERS: '/dispatchers',
  DISPATCHER_BY_ID: (id: string) => `/dispatchers/${id}`,
  DISPATCHER_ACTIVE_PRECINCT: '/dispatchers/precinct',
  PRECINCTS: '/precincts',

  // Cases
  CASES: '/cases',
  CASES_ENHANCED: '/cases-enhanced',
  CASE_BY_ID: (id: string) => `/cases/${id}`,
  CASE_APPROVE: (id: string) => `/cases/${id}/approve`,
  CASE_CLOSE: (id: string) => `/cases/${id}/close`,
  CASE_TRANSITION: (id: string) => `/cases/${id}/transition`,
  CASE_UPLOAD: '/cases/upload',
  CASE_MAKE_OFFLINE: (id: string) => `/cases/${id}/makeoffline`,
  ENTITIES: '/entities',

  // Roster
  ROSTERS: '/roster',
  ROSTER_BY_ID: (id: string) => `/roster/${id}`,
  ROSTER_CREATE: `/roster`,
  ROSTER_DELETE: (id: string) => `/roster/${id}`,
  ROSTER_UPDATE: (id: string) => `/roster/${id}`,
  SHIFT_STRUCTURE_CREATE: '/shift-structure',

  // Notifications
  NOTIFICATIONS: '/notifications',
  NOTIFICATION_BY_ID: (id: string) => `/notifications/${id}`,
  NOTIFICATIONS_REGISTER: '/notifications/register',

  // Softphone
  SOFTPHONE_CREDENTIALS: '/softphone/credentials',
  CALLS_START: '/calls/start',
  CALLS_ACTIVE: '/calls/active',
  CALLS_HISTORY: '/calls/history',
  CALLS_QUEUE_STATUS: '/calls/queue/status',
  CALLS_STATISTICS: '/calls/statistics',
  CALLS_HEALTH: '/calls/health',
  CALL_BY_ID: (id: string) => `/calls/${id}`,
  CALL_ACCEPT: (id: string) => `/calls/${id}/accept`,
  CALL_REJECT: (id: string) => `/calls/${id}/reject`,
  CALL_HANGUP: (id: string) => `/calls/${id}/hangup`,
  CALL_TRANSFER: (id: string) => `/calls/${id}/transfer`,
  CALL_HOLD: (id: string) => `/calls/${id}/hold`,
  CALL_UNHOLD: (id: string) => `/calls/${id}/unhold`,

  // Inquiries
  INQUIRIES: '/inquiry',
  INQUIRY_BY_ID: (id: string) => `/inquiry/${id}`,

  // References
  REFERENCES: '/references',
  RECOMMENDATION_DIAGNOSIS: '/recommendation/diagnosis',

  // Responders
  RESPONDERS: '/responders',
  RESPONDER_BY_ID: (id: string) => `/responders/${id}`,

  // Ambulance Services
  AMBULANCE_PROVIDERS: '/ambulance-providers',
  AMBULANCE_PRICING: '/ambulance-requests/pricing',

  // Shift
  SHIFTS: '/shift',
  SHIFT_BY_ID: (id: string) => `/shift/${id}`,
  SHIFT_CREATE: '/shift',
  SHIFT_UPDATE: (id: string) => `/shift/${id}`,
  SHIFT_DELETE: (id: string) => `/shift/${id}`,
  SHIFT_CLOCK_IN: '/shift/clock-in',
  SHIFT_CLOCK_OUT: '/shift/clock-out',
  SHIFT_SWAP_REQUESTS: '/shift/swaps',
  SHIFT_SWAP_ACTION: '/shift/swap/action',
  SHIFT_DISPATCHERS: (id: string) => `/shift/${id}/dispatchers`,
  SHIFT_SWAP_REQUEST: '/shift/swap/request',
  SHIFT_DEFECTS: '/shift-defects',
  SHIFT_DEFECT_ACTION: '/shift-defects/action',

  // Uploads
  UPLOAD_IMAGE: '/upload/image',

  //report
  REPORTS: '/report',

  // External APIs
  NHIS_VERIFY: (phone: string) =>
    `http://digicomme.com:8000/api/prz/enrollees/${phone}/verify`,
  DISPATCH_TRIAGE: '/dispatch-triage',
} as const

// Query Keys Factory
export const createQueryKeys = (entity: string) => ({
  all: [entity] as const,
  lists: () => [entity, 'list'] as const,
  list: (filters?: Record<string, any>) => [entity, 'list', filters] as const,
  details: () => [entity, 'detail'] as const,
  detail: (id: string) => [entity, 'detail', id] as const,
  infinite: (filters?: Record<string, any>) =>
    [entity, 'infinite', filters] as const,
})

// Common Query Keys
export const QUERY_KEYS = {
  dispatchers: createQueryKeys('dispatchers'),
  precincts: createQueryKeys('precincts'),
  cases: createQueryKeys('cases'),
  notifications: createQueryKeys('notifications'),
  inquiries: createQueryKeys('inquiries'),
  references: createQueryKeys('references'),
  ambulanceProviders: createQueryKeys('ambulance-providers'),
  ambulancePricing: createQueryKeys('ambulance-pricing'),
  precinct: createQueryKeys('precinct'),
  triage: createQueryKeys('dispatch-triage'),
  rosters: createQueryKeys('rosters'),
  shifts: createQueryKeys('shifts'),
  softphone: createQueryKeys('softphone'),
  patientEntities: createQueryKeys('patient-entities'),
} as const

// Mutation Keys
export const MUTATION_KEYS = {
  // Dispatcher mutations
  UPDATE_DISPATCHER: 'updateDispatcher',

  // Case mutations
  CREATE_CASE: 'cases-enhanced',
  UPDATE_CASE: 'updateCase',
  APPROVE_CASE: 'approveCase',
  CLOSE_CASE: 'closeCase',
  TRANSITION_CASE: 'transitionCase',
  UPLOAD_CASE: 'uploadCase',
  INITIATE_USSD_CASE: 'initiateUssdCase',

  // Notification mutations
  MARK_NOTIFICATION_READ: 'markNotificationRead',
  REGISTER_FIREBASE_TOKEN: 'registerFirebaseToken',

  // Softphone mutations
  SOFTPHONE_START_CALL: 'softphoneStartCall',
  SOFTPHONE_ACCEPT_CALL: 'softphoneAcceptCall',
  SOFTPHONE_REJECT_CALL: 'softphoneRejectCall',
  SOFTPHONE_HANGUP_CALL: 'softphoneHangupCall',
  SOFTPHONE_HOLD_CALL: 'softphoneHoldCall',
  SOFTPHONE_UNHOLD_CALL: 'softphoneUnholdCall',
  SOFTPHONE_TRANSFER_CALL: 'softphoneTransferCall',

  // Responder mutations
  RESPONDERS: 'responders',
  NOTIFY_RESPONDER: 'notifyResponder',

  // Inquiry mutations
  CREATE_INQUIRY: 'createInquiry',
  UPDATE_INQUIRY: 'updateInquiry',

  // Roster mutations
  CREATE_ROSTER: 'createRoster',
  CREATE_SHIFT_STRUCTURE: 'createShiftStructure',
  UPDATE_ROSTER: 'updateRoster',
  DELETE_ROSTER: 'deleteRoster',

  // Shift mutations
  CREATE_SHIFT: 'createShift',
  UPDATE_SHIFT: 'updateShift',
  DELETE_SHIFT: 'deleteShift',
  CLOCK_IN_SHIFT: 'clockInShift',
  CLOCK_OUT_SHIFT: 'clockOutShift',

  // External API mutations
  VERIFY_NHIS: 'verifyNhis',
  DISPATCH_TRIAGE: 'dispatchTriage',
} as const

export default apiConfig
